import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Box, Button, Card, CardContent, Chip, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import {
  getPendingPlatformCommission,
  getFeeRequests,
  confirmFeeSent,
  confirmFeeSentBulk,
} from "../../api/deliveryCompanyService";
import { formatCurrency, formatDate } from "../Admin/constants";

// The company's side of paying the platform its commission - mirrors how stores request their
// money from the company: Big Market requests it, the company sends it and clicks "I Sent It",
// Big Market confirms receipt.
const VIEW_TABS = [
  { value: "notRequested", labelKey: "dcFees:tabNotRequested" },
  { value: "requested", labelKey: "dcFees:tabRequested" },
  { value: "sent", labelKey: "dcShared:sentAwaitingConfirmation" },
  { value: "history", labelKey: "dcShared:history" },
];

const SOURCE_LABEL_KEYS = {
  Store: "dcFees:sourceStore",
  Deliveries: "dcFees:sourceDeliveries",
};

// SettlementStatus: Generated = 1 (requested, not yet received), Paid = 2
const STATUS_GENERATED = 1;
const STATUS_PAID = 2;

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TotalFooter({ amount, t }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
      <Typography variant="subtitle2">{t("dcShared:totalWithAmount", { amount: formatCurrency(amount) })}</Typography>
    </Box>
  );
}

export default function PlatformCommissionSection() {
  const { t } = useTranslation(["dcFees", "dcShared"]);
  const [view, setView] = useState("notRequested");
  const [pendingRows, setPendingRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busySettlementId, setBusySettlementId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([getPendingPlatformCommission(), getFeeRequests()])
      .then(([pending, allRequests]) => {
        setPendingRows(pending);
        setRequests(allRequests);
        setError("");
      })
      .catch(() => setError("dcFees:pendingLoadError"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  const requestedRows = useMemo(
    () => requests.filter((r) => r.status === STATUS_GENERATED && !r.companyConfirmedSentAt),
    [requests]
  );
  const sentRows = useMemo(
    () => requests.filter((r) => r.status === STATUS_GENERATED && !!r.companyConfirmedSentAt),
    [requests]
  );
  const historyRows = useMemo(() => requests.filter((r) => r.status === STATUS_PAID), [requests]);

  const sum = (rows, field) => rows.reduce((acc, r) => acc + (r[field] || 0), 0);
  const totals = {
    notRequested: sum(pendingRows, "totalAmount"),
    requested: sum(requestedRows, "amount"),
    sent: sum(sentRows, "amount"),
    paid: sum(historyRows, "amount"),
  };

  async function handleConfirmSent(row) {
    setBusySettlementId(row.commissionSettlementId);
    try {
      await confirmFeeSent(row.commissionSettlementId);
      load();
    } catch {
      setError("dcFees:confirmError");
    } finally {
      setBusySettlementId(null);
    }
  }

  async function handleSendAll() {
    const ids = requestedRows.map((r) => r.commissionSettlementId);
    if (ids.length === 0) return;
    setSendingAll(true);
    try {
      await confirmFeeSentBulk(ids);
      load();
    } catch {
      setError("dcFees:sendAllError");
    } finally {
      setSendingAll(false);
    }
  }

  const pendingColumns = useMemo(
    () => [
      { field: "storeName", headerName: t("dcShared:store"), flex: 1, minWidth: 180 },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 90 },
      {
        field: "productCommissionAmount",
        headerName: t("dcFees:productCommission"),
        width: 170,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "deliveryCommissionAmount",
        headerName: t("dcFees:deliveryCommission"),
        width: 170,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "totalAmount", headerName: t("dcShared:total"), width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "oldestConfirmedAt", headerName: t("dcFees:since"), width: 160, valueFormatter: (v) => formatDate(v) },
    ],
    [t]
  );

  const requestColumns = useMemo(() => {
    const base = [
      {
        field: "source",
        headerName: t("dcFees:storeSource"),
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={SOURCE_LABEL_KEYS[params.value] ? t(SOURCE_LABEL_KEYS[params.value]) : params.value}
            color={params.value === "Store" ? "primary" : "secondary"}
          />
        ),
      },
      { field: "label", headerName: t("dcShared:store"), flex: 1, minWidth: 180 },
      { field: "periodStart", headerName: t("dcShared:from"), width: 140, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: t("dcShared:to"), width: 140, valueFormatter: (v) => formatDate(v) },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 90 },
      { field: "amount", headerName: t("dcShared:amount"), width: 130, valueFormatter: (v) => formatCurrency(v) },
    ];

    if (view === "requested") {
      return [
        ...base,
        { field: "generatedAt", headerName: t("dcFees:requestedAt"), width: 160, valueFormatter: (v) => formatDate(v) },
        {
          field: "send",
          headerName: "",
          width: 140,
          sortable: false,
          renderCell: (params) => (
            <Button
              size="small"
              variant="contained"
              disabled={busySettlementId === params.row.commissionSettlementId}
              onClick={() => handleConfirmSent(params.row)}
            >
              {t("dcFees:iSentIt")}
            </Button>
          ),
        },
      ];
    }

    if (view === "sent") {
      return [
        ...base,
        { field: "companyConfirmedSentAt", headerName: t("dcShared:sentAt"), width: 160, valueFormatter: (v) => formatDate(v) },
      ];
    }

    return [
      ...base,
      { field: "companyConfirmedSentAt", headerName: t("dcShared:sentAt"), width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "paidAt", headerName: t("dcFees:receivedAt"), width: 160, valueFormatter: (v) => formatDate(v) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, busySettlementId, t]);

  const tableRows = view === "requested" ? requestedRows : view === "sent" ? sentRows : historyRows;
  const emptyKey = {
    notRequested: "dcFees:emptyNotRequested",
    requested: "dcFees:emptyRequested",
    sent: "dcFees:emptySent",
    history: "dcFees:emptyHistory",
  }[view];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("dcFees:sectionDescription")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={AccountBalanceWalletOutlinedIcon}
            label={t("dcFees:statNotRequested")}
            value={formatCurrency(totals.notRequested)}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={HourglassTopOutlinedIcon}
            label={t("dcFees:statRequested")}
            value={formatCurrency(totals.requested)}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={MarkEmailReadOutlinedIcon}
            label={t("dcFees:statSent")}
            value={formatCurrency(totals.sent)}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PaidOutlinedIcon}
            label={t("dcFees:statTotalPaid")}
            value={formatCurrency(totals.paid)}
            color="success"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Tabs value={view} onChange={(e, v) => setView(v)} variant="scrollable" allowScrollButtonsMobile>
          {VIEW_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.labelKey)} />
          ))}
        </Tabs>

        {view === "requested" && (
          <Button variant="contained" disabled={sendingAll || requestedRows.length === 0} onClick={handleSendAll}>
            {t("dcFees:iSentAll", { count: requestedRows.length })}
          </Button>
        )}
      </Box>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        {view === "notRequested" ? (
          <DataGrid
            autoHeight
            rows={pendingRows}
            getRowId={(row) => row.storeId}
            columns={pendingColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
        ) : (
          <DataGrid
            autoHeight
            rows={tableRows}
            getRowId={(row) => row.commissionSettlementId}
            columns={requestColumns}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
        )}
        {!loading && (view === "notRequested" ? pendingRows : tableRows).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t(emptyKey)}
          </Typography>
        )}
        {!loading && view !== "history" && (
          <TotalFooter
            amount={view === "notRequested" ? totals.notRequested : view === "requested" ? totals.requested : totals.sent}
            t={t}
          />
        )}
      </Box>
    </Box>
  );
}
